import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrayerChoiceAnalytics } from './usePrayerChoiceAnalytics';
import { useEnhancedPrayerGeneration } from './useEnhancedPrayerGeneration';
import { criticalLogger } from '@/utils/logger';

interface PrayerContext {
  id: string;
  title: string;
  content: string;
}

export function usePrayerChoiceDialog() {
  // Initialize all state variables with stable initial values
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [prayerContext, setPrayerContext] = useState<PrayerContext | undefined>(undefined);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState<boolean>(false);
  
  const analytics = usePrayerChoiceAnalytics();
  const { recordChoiceAction } = useEnhancedPrayerGeneration();
  const navigate = useNavigate();

  const openChoiceDialog = (context?: PrayerContext, sourceContext = 'unknown') => {
    console.log('Opening choice dialog with context:', context);
    setPrayerContext(context);
    setIsOpen(true);
    setIsMinimized(false);
    
    // Track dialog opening
    if (context) {
      analytics.trackChoiceDialogOpened(context.id, sourceContext);
    }
  };

  const closeChoiceDialog = () => {
    console.log('Closing choice dialog');
    setIsOpen(false);
    setIsMinimized(false);
    setPrayerContext(undefined);
  };

  const minimizeChoiceDialog = () => {
    console.log('Minimizing choice dialog');
    setIsOpen(true); // Keep open but minimized
    setIsMinimized(true);
    // Keep prayer context when minimized
  };

  const restoreChoiceDialog = () => {
    console.log('Restoring choice dialog');
    setIsMinimized(false);
    setIsOpen(true);
  };

  const handleINeedPrayer = async () => {
    console.log('handleINeedPrayer called with context:', prayerContext);
    if (!prayerContext) {
      console.log('No prayer context available');
      return;
    }

    // Track the choice selection
    analytics.trackChoiceSelected(prayerContext.id, 'i_need_prayer');

    // Record the choice action with enhanced tracking
    try {
      await recordChoiceAction(prayerContext.id, 'i_need_prayer', 'ai_assistant');
    } catch (error) {
      criticalLogger.error('Failed to record choice action:', error);
    }

    // Record the choice action and get the ID for future reference
    const choiceActionId = await analytics.recordChoiceAction(
      prayerContext.id, 
      'i_need_prayer', 
      'ai_assistant'
    );

    console.log('Opening AI Prayer Assistant');
    closeChoiceDialog();
    
    // Track AI assistant selection
    analytics.trackMethodSelected(prayerContext.id, 'ai_assistant');
    
    // Open the AI Assistant modal
    setIsAiAssistantOpen(true);
    
    // Store choice action ID for potential future use
    if (choiceActionId) {
      console.log('Choice action recorded with ID:', choiceActionId);
    }
  };

  const handleIWantToPray = async () => {
    console.log('handleIWantToPray called with context:', prayerContext);
    if (!prayerContext) {
      console.log('No prayer context available');
      return;
    }

    // Track the choice selection
    analytics.trackChoiceSelected(prayerContext.id, 'i_want_to_pray');

    // Record the choice action with enhanced tracking
    try {
      await recordChoiceAction(prayerContext.id, 'i_want_to_pray', 'manual_prayer');
    } catch (error) {
      criticalLogger.error('Failed to record choice action:', error);
    }

    // Record the choice action
    const choiceActionId = await analytics.recordChoiceAction(
      prayerContext.id, 
      'i_want_to_pray', 
      'manual_prayer'
    );

    console.log('Closing choice dialog and navigating to share prayer page');
    closeChoiceDialog();
    
    // Track manual prayer completion
    analytics.trackMethodSelected(prayerContext.id, 'manual_prayer');
    
    // Navigate to share prayer page instead of opening modal
    console.log('Navigating to /share-prayer');
    navigate('/share-prayer');

    console.log('Manual prayer recorded for:', prayerContext.title);
    
    // Record that user prayed for this prayer
    if (choiceActionId) {
      await analytics.recordPrayedFor(prayerContext.id, choiceActionId, 'manual');
    }
  };

  const closeAiAssistant = () => {
    console.log('Closing AI assistant');
    setIsAiAssistantOpen(false);
  };

  // Reset dialog state when component mounts to prevent stale state from previous navigation
  useEffect(() => {
    console.log('[usePrayerChoiceDialog] Component mounted, resetting state to prevent stale dialog state');
    setIsOpen(false);
    setIsMinimized(false);
    setPrayerContext(undefined);
    setIsAiAssistantOpen(false);
  }, []);

  // Debug logging removed to prevent excessive console output

  return {
    isOpen,
    isMinimized,
    prayerContext,
    isAiAssistantOpen,
    openChoiceDialog,
    closeChoiceDialog,
    minimizeChoiceDialog,
    restoreChoiceDialog,
    handleINeedPrayer,
    handleIWantToPray,
    closeAiAssistant
  };
}
